"use client";

import { Compass } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { AspectRatio } from "@/components/ui/aspect-ratio";
export default function RelatedGamingArticles() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const divider = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const disciplines = [{
    number: "01",
    title: "Gaming VPN Speed",
    description: "Optimize your ping and latency with our high-speed servers. Engineered for competitive play, ensuring zero lag during intense sessions.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_vpn_speed.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_vpn_speed.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_vpn_speed.webp-wvc-sizes",
    alt: "High speed gaming connection visualization",
    span: "lg:col-span-5",
    height: "lg:h-[520px]",
    offset: ""
  }, {
    number: "02",
    title: "Secure Gaming Hub",
    description: "Protect your identity from DDoS attacks and swatting. Our robust encryption keeps your gaming profile safe from malicious actors.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/secure_gaming_hub.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/secure_gaming_hub.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/secure_gaming_hub.png-wvc-sizes",
    alt: "Secure gaming network protection shield",
    span: "lg:col-span-4",
    height: "lg:h-[420px]",
    offset: "lg:mt-24"
  }, {
    number: "03",
    title: "Global Server Access",
    description: "Unlock regional game content and play with friends worldwide. Bypass geo-restrictions effortlessly with our global server network.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/global_server_access.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/global_server_access.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/global_server_access.png-wvc-sizes",
    alt: "Global map showing gaming server locations",
    span: "lg:col-span-3",
    height: "lg:h-[580px]",
    offset: ""
  }];
  return <section data-section-id="3193" id="relatedgamingarticles" className="bg-background text-foreground py-24 md:py-32 lg:py-[180px] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end mb-20 md:mb-28">
          <div ref={header.ref} className={`lg:col-span-7 transition-all duration-[900ms] ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase text-muted-foreground mb-8">
              Section 01 — Gaming
            </p>
            <h2 className="font-serif font-default font-light text-4xl md:text-5xl lg:text-[72px] leading-[1.05] tracking-tight text-foreground">
              Ultimate gaming speed,
              <br />
              <span className="italic text-primary/90">
                secured for victory.
              </span>
            </h2>
          </div>

          <div ref={quote.ref} className={`lg:col-span-5 lg:text-right transition-all duration-[1100ms] delay-200 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-serif italic text-xl md:text-2xl leading-snug text-muted-foreground max-w-xs lg:ml-auto">
              "Victory demands zero latency."
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {disciplines.map((item, i) => {
          const card = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div data-index={i} ref={card.ref} key={i} className={`group ${item.span} ${item.offset} transition-all duration-[900ms] ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <article className={`relative h-full ${item.height} bg-card text-card-foreground border border-border rounded-3xl shadow-sm overflow-hidden flex flex-col transition-all duration-500 ease-out group-hover:-translate-y-1 group-hover:shadow-lg`}>
                  <div className="overflow-hidden">
                    <AspectRatio ratio={4 / 5}>
                      <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-[1.02]" />
                    </AspectRatio>
                  </div>

                  <div className="flex flex-col flex-1 p-6 md:p-8">
                    <div className="h-px w-full bg-border mb-5" />
                    <p className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">
                      {item.number} / 03
                    </p>
                    <h3 className="relative font-serif font-default text-2xl md:text-[28px] leading-tight text-foreground mb-3 inline-block w-fit">
                      {item.title}
                      <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-500 ease-out group-hover:w-full" />
                    </h3>
                    <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </article>
              </div>;
        })}
        </div>

        {}
        <div ref={divider.ref} className={`mt-24 md:mt-32 flex items-center gap-6 transition-all duration-[1000ms] ease-out ${divider.isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="h-px flex-1 bg-border" />
          <Compass className="size-5 text-chart-3" strokeWidth={1.25} aria-hidden="true" />
          <div className="h-px flex-1 bg-border" />
        </div>
      </div>
    </section>;
}